import Button from "components/common/Button";
import CustomTable from "components/common/CustomTable";
import Dropdown from "components/common/Dropdown";
import Loader from "components/common/Loader";
import VendorAvatar from "components/common/VendorAvatar";
import { formatMoney, getCurrencyFromCurrencyCode, onScroll } from "helpers";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { usePinPurchaseOrder, useBulkPinPurchaseOrder } from "hooks/orderHooks";
import { debounce, truncate, uniqBy } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { orderActions } from "redux/Ldb/actions";
import { AccountPermissions } from "types/permissions";
import toast from "react-hot-toast";
import CustomToast from "components/common/CustomToast";
import FloatingActionBar from "components/common/FloatingActionBar";
import SearchTextField from "components/common/InputField/SearchTextField";
import PurchaseOrderTableAction from "./PurchaseOrderTableAction";
import Paper from "components/common/Paper";
import clsx from "clsx";
import { useInfiniteScrollV2 } from "hooks/useInfiniteScrollV2";
import ModalContainer from "components/common/ModalContainer";

const PurchaseOrder = () => {
    const limit = 20;
    const [activeTabTwo, setActiveTabTwo] = useState(0);
    const [loadMorePurchaseOrder, setLoadMorePurchaseOrder] = useState(false);
    const [status, setStatus] = useState("");
    const [purchaseOrderList, setPurchaseOrderList] = useState([]);
    const [purchaseOrderPagination, setPurchaseOrderPagination] = useState<{
        current: string | number;
        number_of_pages: string | number;
    }>({
        current: "",
        number_of_pages: "",
    });

    const [poDataMetrics, setPoDataMetrics] = useState<{
        approvedPO: 0;
        pendingPO: 0;
        rejectedPO: 0;
        fulfilledPO: 0;
        totalPO: 0;
        issuedPO: 0;
    }>();

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [poPage, setPoPage] = useState<number>(1);
    const [starredFilter, setStarredFilter] = useState("all");
    const [selectedPO, setSelectedPO] = useState<any[]>([]);
    const [approvalStatus, setApprovalStatus] = useState("");
    const [archiveModal, setArchiveModal] = useState<boolean>(false);
    const [unarchiveModal, setUnarchiveModal] = useState<boolean>(false);
    const [singlePOArchive, setSinglePOArchive] = useState<string[]>([]);
    const [singlePOUnarchive, setSinglePOUnarchive] = useState<string[]>([]);
    const dashboardContentId = "purchase-order-table";
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Pin/Unpin functionality
    const { handlePin, handleUnpin, isPinning, isUnpinning, pinSuccess, unpinSuccess } =
        usePinPurchaseOrder();
    const {
        handleBulkPin,
        handleBulkUnpin,
        isPinning: isBulkPinning,
        isUnpinning: isBulkUnpinning,
        bulkPinSuccess,
        bulkUnpinSuccess,
    } = useBulkPinPurchaseOrder();

    const {
        fetchingPurchaseOrderV2,
        fetchedPurchaseOrderV2Success,
        fetchedPOMetricsSuccess,
        archivingPurchaseOrder,
        unarchivingPurchaseOrder,
        archivePurchaseOrderSuccess,
        unarchivePurchaseOrderSuccess,
    } = useAppSelector((state) => state.order);

    useEffect(() => {
        setPoPage(1);
        setPurchaseOrderList([]);
        dispatch(orderActions.resetPurchaseOrderV2());
        if (activeTabTwo === 0) {
            setStatus("");
            setApprovalStatus("");
        } else if (activeTabTwo === 1) {
            setStatus("completed");
            setApprovalStatus("");
        } else if (activeTabTwo === 2) {
            setStatus("");
            setApprovalStatus("pending");
        } else if (activeTabTwo === 3) {
            setStatus("in-transit");
            setApprovalStatus("");
        } else if (activeTabTwo === 4) {
            setStatus("");
            setApprovalStatus("draft");
        } else if (activeTabTwo === 5) {
            setStatus("");
            setApprovalStatus("archived");
        }
    }, [activeTabTwo]);

    useInfiniteScrollV2(
        useMemo(
            () => ({
                containerId: dashboardContentId,
                onLoadMore: () => {
                    setPoPage(() => poPage + 1);
                    setLoadMorePurchaseOrder(true);
                },
                loading: fetchingPurchaseOrderV2 || false,
                hasMore:
                    purchaseOrderPagination?.current < purchaseOrderPagination?.number_of_pages,
                enabled: true,
                offset: 50,
            }),
            [dashboardContentId, fetchingPurchaseOrderV2, poPage, purchaseOrderPagination]
        )
    );

    const handleDebouncedChange = debounce((e) => {
        setPoPage(1);
        const { name, value } = e.target;
        setDebouncedSearch(value);
    }, 1000);

    const handleBulkArchive = (poIds: string[]) => {
        dispatch(orderActions.archivePurchaseOrder({ poIds: poIds }));
    };

    const handleBulkUnarchive = (poIds: string[]) => {
        dispatch(orderActions.unarchivePurchaseOrder({ poIds: poIds }));
    };

    const handleSearch = (e) => {
        setPoPage(1);
        const { name, value } = e.target;
        setSearch(value);
    };

    useEffect(() => {
        dispatch(orderActions.getPOMetrics());
    }, [dispatch]);

    useEffect(() => {
        if (Boolean(fetchedPOMetricsSuccess)) {
            setPoDataMetrics(fetchedPOMetricsSuccess?.metrics);
        }
    }, [fetchedPOMetricsSuccess]);

    useEffect(() => {
        if (Boolean(fetchedPurchaseOrderV2Success)) {
            setPurchaseOrderList((prev) =>
                poPage > 1
                    ? [...prev, ...fetchedPurchaseOrderV2Success?.po]
                    : [...fetchedPurchaseOrderV2Success?.po]
            );
            setPurchaseOrderPagination({
                current: fetchedPurchaseOrderV2Success?.pagination.current,
                number_of_pages: fetchedPurchaseOrderV2Success?.pagination?.number_of_pages,
            });

            dispatch(orderActions.resetPurchaseOrderV2());
            setLoadMorePurchaseOrder(false);
        }
    }, [fetchedPurchaseOrderV2Success, status, approvalStatus]);

    useEffect(() => {
        if (
            pinSuccess ||
            unpinSuccess ||
            bulkPinSuccess ||
            bulkUnpinSuccess ||
            starredFilter ||
            poPage
        ) {
            setPoPage(poPage || 1);
            dispatch(
                orderActions.getPurchaseOrderV2(
                    limit,
                    poPage ?? 1,
                    debouncedSearch,
                    "",
                    status,
                    starredFilter,
                    approvalStatus !== "archived" ? approvalStatus : "",
                    approvalStatus === "archived" ? "true" : ""
                )
            );
        }
    }, [
        pinSuccess,
        unpinSuccess,
        bulkPinSuccess,
        bulkUnpinSuccess,
        dispatch,
        limit,
        poPage,
        debouncedSearch,
        status,
        starredFilter,
        approvalStatus,
        unarchivePurchaseOrderSuccess,
        archivePurchaseOrderSuccess,
    ]);

    useEffect(() => {
        if (!!bulkPinSuccess || !!pinSuccess) {
            setPoPage(1);
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title={`${bulkPinSuccess ? selectedPO?.length : 1} Purchase Order(s) pinned`}
                    message="These purchase orders have been added to your Pinned list."
                    type="success"
                />
            ));
            setSelectedPO([]);
        }
    }, [bulkPinSuccess, pinSuccess]);

    useEffect(() => {
        if (!!bulkUnpinSuccess || !!unpinSuccess) {
            setPoPage(1);
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title={`${
                        bulkUnpinSuccess ? selectedPO?.length : 1
                    } Purchase Order(s) unpinned`}
                    message="These purchase orders have been removed from your pinned list."
                    type="warning"
                />
            ));
            setSelectedPO([]);
        }
    }, [bulkUnpinSuccess, unpinSuccess]);

    useEffect(() => {
        if (Boolean(archivePurchaseOrderSuccess)) {
            setPoPage(1);
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title={"PO Archived"}
                    message="The purchase order has been successfully archived and removed from your active list."
                    type="success"
                />
            ));
            setSelectedPO([]);
            dispatch(orderActions.resetArchivePurchaseOrderSuccess());
            setSinglePOArchive([]);
            setArchiveModal(false);
        }
    }, [dispatch, archivePurchaseOrderSuccess]);

    useEffect(() => {
        if (Boolean(unarchivePurchaseOrderSuccess)) {
            setPoPage(1);
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title={"PO Unarchived"}
                    message="The purchase order has been successfully unarchived and added to your active list."
                    type="success"
                />
            ));
            setSelectedPO([]);
            dispatch(orderActions.resetUnarchivePurchaseOrderSuccess());
            setSinglePOUnarchive([]);
            setUnarchiveModal(false);
        }
    }, [dispatch, unarchivePurchaseOrderSuccess]);

    const tableHeaderTwo = [
        { title: "PO ID", widthClass: "w-[15%]" },
        { title: "Supplier", widthClass: "w-[11%]" },
        { title: "Created by", widthClass: "w-[10.6%]" },
        { title: "Product name", widthClass: "w-[11%]" },
        { title: "PO Value", widthClass: "w-[7.9%]" },
        { title: "Quantity", widthClass: "w-[7.9%]" },
        { title: "Shipments", widthClass: "w-[9%]" },
        { title: "Status", widthClass: "w-[9%]" },
        {
            title: "Created date",
            widthClass: "w-[9%]",
            tooltipIcon: "created-date",
            tooltipContent: "Date PO was created",
            tooltipStyle: {
                width: "180px !important",
            },
        },
        {
            title: "Issued date",
            widthClass: "w-[9%]",
            tooltipIcon: "issued-date",
            tooltipContent: "Date PO was sent/confirmed",
            tooltipStyle: {
                width: "180px !important",
            },
        },
        {
            title: "Due date",
            widthClass: "w-[9%]",
            tooltipIcon: "due-date",
            tooltipContent: "Date of PO deadline for payment or delivery.",
            tooltipStyle: {
                width: "180px !important",
            },
        },
        { title: "", widthClass: "w-[5.1%]" },
    ];

    const tableBodyTwo = purchaseOrderList?.map((po, idx) => [
        {
            content: (
                <div
                    key={idx}
                    className={`py-3 pr-3 flex font-rocGroteskMedium items-center h-full border-slate-100 text-sm text-slate-500 ${
                        !status && !approvalStatus ? "uppercase" : ""
                    }`}
                >
                    <div className="flex items-center space-x-2">
                        <p className={!status && !approvalStatus ? "break-all" : ""}>
                            {po?.poPrefix && `${po?.poPrefix}-`}
                            {po?.taId}
                        </p>
                    </div>
                </div>
            ),
            cellClickAction: () =>
                navigate(`/dashboard/purchase/shipment/${po?._id}`, {
                    state: { internalNavigation: true },
                }),
            isItemChecked: selectedPO?.some((item) => item._id === po._id),
            onCheckBoxClick: () => {
                if (selectedPO?.some((item) => item._id === po._id)) {
                    setSelectedPO(selectedPO.filter((item) => item._id !== po._id));
                } else {
                    setSelectedPO([...selectedPO, po]);
                }
            },
            checkboxEndAdornment: (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        if (po?.isPinned) {
                            handleUnpin(po?._id);
                        } else {
                            handlePin(po?._id);
                        }
                    }}
                    disabled={isPinning || isUnpinning || fetchingPurchaseOrderV2}
                    className={`${
                        isPinning || isUnpinning || fetchingPurchaseOrderV2
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                    }`}
                >
                    <i
                        className={`ri-star-fill text-lg ${
                            po?.isPinned ? "text-[#FCD34D]" : "text-[#E2E8F0]"
                        }`}
                        style={{ cursor: "pointer" }}
                        title={po?.isPinned ? "Unpin order" : "Pin order"}
                    ></i>
                </button>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-800 `}
                >
                    <div className="flex items-center space-x-3">
                        <VendorAvatar
                            imageSrc={po?.vendor?.profile?.avatar}
                            size={32}
                            name={po?.vendor?.email}
                        />
                        <span className="break-all">{po?.vendor?.profile?.businessName}</span>
                    </div>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                >
                    <div className="flex items-center space-x-2">
                        <VendorAvatar
                            imageSrc={po?.createdBy?.profile?.avatar}
                            size={24}
                            name={po?.createdBy?.fullName}
                        />
                        <span className="break-all">{po?.createdBy?.fullName}</span>
                    </div>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                >
                    <span className="break-all">
                        {truncate(po?.inventory?.productName, { length: 30 })}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                >
                    <span className="break-all">
                        {getCurrencyFromCurrencyCode(po?.totalAmount?.currency)}
                        {formatMoney().format(po?.totalAmount?.amount)}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500 `}
                >
                    <span className="break-all">
                        {formatMoney().format(Number(po?.totalNumberOfItems))}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500`}
                >
                    <span className="break-all">
                        {formatMoney().format(Number(po?.numberOfShipments))}
                    </span>
                </div>
            ),
        },

        {
            content: (
                <div className={`py-3 flex items-center h-full border-slate-100`}>
                    <div
                        className={clsx(
                            `rounded-[16px]`,
                            (status === "completed" || approvalStatus === "completed") &&
                                "bg-g-25 text-g-60 !text-g-60",
                            (status === "in-transit" || approvalStatus === "in-transit") &&
                                "bg-[#EFEFFC] text-[#6562E5]",
                            (status === "pending" || approvalStatus === "pending") &&
                                "bg-light-yellow-status text-[#E5993E]",
                            (status === "draft" || approvalStatus === "draft") &&
                                "bg-light-yellow-status text-[#E5993E]",
                            !status &&
                                !approvalStatus &&
                                po?.currentStatus?.toLowerCase() === "rejected" &&
                                "bg-r-25 text-r-55",
                            !status &&
                                !approvalStatus &&
                                po?.currentStatus?.toLowerCase() === "in-transit" &&
                                "bg-[#EFEFFC] text-[#6562E5]",
                            !status &&
                                !approvalStatus &&
                                po?.currentStatus?.toLowerCase() === "completed" &&
                                "bg-g-25 text-g-60",
                            !status &&
                                !approvalStatus &&
                                po?.currentStatus?.toLowerCase() === "pending" &&
                                "bg-light-yellow-status text-[#E5993E]",
                            po?.isArchived && "!bg-[#E2E8F0] !text-[#475569]"
                        )}
                    >
                        <p className="text-sm font-rocGroteskMedium text-center px-2">
                            {/* Status-based text */}
                            {po?.isArchived
                                ? "Archived"
                                : status === "completed" || approvalStatus === "completed"
                                ? "Fulfilled"
                                : status === "in-transit" || approvalStatus === "in-transit"
                                ? "Pending fulfilment"
                                : status === "pending" || approvalStatus === "pending"
                                ? "Pending"
                                : status === "draft" || approvalStatus === "draft"
                                ? "Draft"
                                : !status &&
                                  !approvalStatus &&
                                  po?.currentStatus?.toLowerCase() === "rejected"
                                ? "Cancelled"
                                : !status &&
                                  !approvalStatus &&
                                  po?.currentStatus?.toLowerCase() === "in-transit"
                                ? "Pending fulfilment"
                                : !status &&
                                  !approvalStatus &&
                                  po?.currentStatus?.toLowerCase() === "completed"
                                ? "Fulfilled"
                                : !status &&
                                  !approvalStatus &&
                                  po?.currentStatus?.toLowerCase() === "pending"
                                ? "Pending"
                                : !status &&
                                  !approvalStatus &&
                                  po?.currentStatus?.toLowerCase() === "draft" &&
                                  "Draft"}
                        </p>
                    </div>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500`}
                >
                    <span className="break-all">
                        {po?.createdAt ? moment(po?.createdAt).format("MMM DD, YYYY") : ""}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500`}
                >
                    <span className="break-all">
                        {po?.issuedDate ? moment(po?.issuedDate).format("MMM DD, YYYY") : ""}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <div
                    className={`py-3 pr-3 font-rocGroteskMedium border-slate-100 text-sm text-slate-500`}
                >
                    <span className="break-all">
                        {po?.dueDate ? moment(po?.dueDate).format("MMM DD, YYYY") : ""}
                    </span>
                </div>
            ),
        },
        {
            content: (
                <PurchaseOrderTableAction
                    key={idx}
                    po={po}
                    setSelectedPOs={setSelectedPO}
                    setArchiveModal={setArchiveModal}
                    setUnarchiveModal={setUnarchiveModal}
                    setSinglePOArchive={setSinglePOArchive}
                    setSinglePOUnarchive={setSinglePOUnarchive}
                    refreshList={() => {
                        dispatch(orderActions.getPOMetrics());
                        dispatch(
                            orderActions.getPurchaseOrderV2(
                                limit,
                                1,
                                debouncedSearch,
                                "",
                                status,
                                starredFilter,
                                approvalStatus
                            )
                        );
                    }}
                />
            ),
        },
    ]);

    const statusDropdownOptions = [
        {
            label: "All Status",
            value: 0,
        },
        {
            label: "Fulfilled",
            value: 1,
        },
        {
            label: "Pending",
            value: 2,
        },

        {
            label: "Pending fulfilment",
            value: 3,
        },

        {
            label: "Draft/Recommended PO",
            value: 4,
        },
        {
            label: "Archived PO's",
            value: 5,
        },
    ];

    const starredDropdownOptions = [
        {
            label: (
                <div>
                    All PO's <span className="text-slate-500">(Pinned and unpinned)</span>
                </div>
            ),
            value: "all",
        },
        {
            label: <div>Pinned PO's only</div>,
            value: "starred",
        },
        {
            label: <div>Unpinned PO's only</div>,
            value: "unstarred",
        },
    ];

    const poMetrics = [
        {
            label: "Total no. of PO",
            value: poDataMetrics?.totalPO || 0,
        },

        {
            label: "Issued PO",
            value: poDataMetrics?.issuedPO || 0,
        },

        {
            label: "Approved PO",
            value: poDataMetrics?.approvedPO || 0,
        },

        {
            label: "Pending PO",
            value: poDataMetrics?.pendingPO || 0,
        },

        {
            label: "Rejected PO",
            value: poDataMetrics?.rejectedPO || 0,
        },

        {
            label: "Fulfilled PO",
            value: poDataMetrics?.fulfilledPO || 0,
        },
    ];

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {poMetrics.map((metric) => (
                    <Paper className="p-[16px]">
                        <p className="text-sm">{metric.label}</p>
                        <h5 className="text-xl font-rocGroteskMedium">{metric.value}</h5>
                    </Paper>
                ))}
            </div>

            <div className="flex w-full items-center space-x-3 mt-[24px]">
                <SearchTextField
                    name={"search"}
                    value={search}
                    inputPlaceholder={"Search PO"}
                    onChange={handleSearch}
                    onInput={handleDebouncedChange}
                />

                <Dropdown
                    handleChange={(name, value) => {
                        setStarredFilter(value);
                    }}
                    dropdown={
                        <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 pl-3 pr-2 space-x-1 rounded-[5px] border border-slate-200">
                            <div className="flex items-center space-x-2">
                                <span className={`text-g-75 font-rocGroteskMedium text-[13px]`}>
                                    {starredDropdownOptions?.find(
                                        (option) => option.value === starredFilter
                                    )?.label || "All Orders"}
                                </span>
                            </div>
                            <i className="ri-arrow-down-s-line text-g-75"></i>
                        </div>
                    }
                    wholeContainerClass="!w-fit max-sm:!w-full"
                    dropdownContainerClasses={`shadow-cardShadow-4 !w-full !max-w-full min-w-full border border-slate-100 rounded !max-h-fit !pt-0 !z-[1400]`}
                    dropdownOptions={starredDropdownOptions}
                />

                <Dropdown
                    handleChange={(name, value) => {
                        setActiveTabTwo(Number(value));
                    }}
                    dropdown={
                        <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 pl-3 pr-2 space-x-1 rounded-[5px] border border-slate-200">
                            <div className="flex items-center space-x-2">
                                <span className={`text-g-75 font-rocGroteskMedium text-[13px]`}>
                                    {statusDropdownOptions?.find(
                                        (option) => option.value === activeTabTwo
                                    )?.label || "All Status"}
                                </span>
                            </div>
                            <i className="ri-arrow-down-s-line text-g-75"></i>
                        </div>
                    }
                    wholeContainerClass="!w-fit max-sm:!w-full"
                    dropdownContainerClasses={`shadow-cardShadow-4 !w-full !max-w-full min-w-full border border-slate-100 rounded !max-h-fit !pt-0 !z-[1400]`}
                    dropdownOptions={statusDropdownOptions}
                />
                <Button
                    btnText={"Settings"}
                    type={"button"}
                    btnPermission={[
                        AccountPermissions.create_purchase_order,
                        AccountPermissions.all_access,
                    ]}
                    btnClassname="!py-2 !px-4 !w-fit max-sm:w-full !whitespace-nowrap !bg-white !border !border-[#F4F5F7]"
                    icon={<i className="ri-settings-3-line text-base"></i>}
                    onClick={() =>
                        navigate("/dashboard/settings/3", {
                            state: { internalNavigation: true },
                        })
                    }
                />

                <Button
                    btnText={"Create PO"}
                    type={"button"}
                    btnPermission={[
                        AccountPermissions.create_purchase_order,
                        AccountPermissions.all_access,
                    ]}
                    btnClassname="!py-2 !px-4 !w-fit !whitespace-nowrap max-sm:w-full"
                    onClick={() =>
                        navigate("/dashboard/purchase/create-purchase-request", {
                            state: { internalNavigation: true },
                        })
                    }
                />
            </div>

            <div className="mt-[16px]">
                {" "}
                <div className="">
                    <div className="max-lg:hidden relative block">
                        <CustomTable
                            tableBody={tableBodyTwo}
                            tableHeader={tableHeaderTwo}
                            isScrollable
                            scrollContentId={dashboardContentId}
                            scrollWithin
                            // isInifiniteScroll
                            isLoading={fetchingPurchaseOrderV2 && !loadMorePurchaseOrder}
                            isCellBordered
                            isCellSelectable={true}
                            // isAllSelectable={true}
                            headerContainerClass="!bg-slate-50 !border-slate-200"
                            headerItemClass="font-rocGroteskMedium "
                            tableClass="!border-slate-200"
                            renderEmptyTable={() => (
                                <div className="items-center flex-col flex  h-[100%]  justify-center my-[8%]">
                                    {(search || starredFilter || approvalStatus) &&
                                    !fetchingPurchaseOrderV2 &&
                                    !loadMorePurchaseOrder ? (
                                        <div className="space-y-3 w-[452px]  justify-center items-center flex flex-col  text-center">
                                            <div className="p-[5px] flex items-center justify-center w-[34px] h-[34px] rounded-lg bg-slate-100 mb-2.5">
                                                <i className="ri-search-line text-2xl text-[#142837] opacity-50"></i>
                                            </div>

                                            <p className="text-slate-500 font-rocGroteskMedium text-[16px]">
                                                Sorry, no results match your search. Refine your
                                                criteria and try again.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3 max-w-[452px]  justify-center items-center flex flex-col  text-center">
                                            <div className="p-[5px] flex items-center justify-center w-[34px] h-[34px] rounded-lg bg-slate-100 mb-2.5">
                                                <i className="ri-guide-fill text-2xl text-light-black opacity-50"></i>
                                            </div>
                                            <p className="text-slate-500 font-rocGroteskMedium text-[16px]">
                                                Looks like there are no purchase orders at the
                                                moment. Start by creating your first purchase order
                                                to keep of your transaction and manage your
                                                procurement efficiently
                                            </p>
                                            <Button
                                                btnText={"Create purchase request"}
                                                btnPermission={[
                                                    AccountPermissions.create_purchase_order,
                                                    AccountPermissions.all_access,
                                                ]}
                                                type={"button"}
                                                btnClassname="!py-2 !px-4 !w-fit !whitespace-nowrap max-sm:w-full"
                                                onClick={() =>
                                                    navigate(
                                                        "/dashboard/purchase/create-purchase-request",
                                                        {
                                                            state: { internalNavigation: true },
                                                        }
                                                    )
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        />
                    </div>
                    <div className="max-lg:block hidden">
                        {purchaseOrderList?.map((po, idx) => (
                            <div className="w-full mt-4 " key={idx}>
                                <div className="flex justify-between items-center border px-2 py-4 sm:px-4 sm:py-4 rounded">
                                    <div className="">
                                        <div>
                                            <div className="flex space-x-3">
                                                <p className="text-[#142837] text-sm font-rocGroteskMedium">
                                                    {po?.poPrefix ? `${po?.poPrefix}-` : "PO-"}
                                                    {po?.taId}
                                                </p>
                                                <div className="border-r"></div>
                                                <p className="text-sm font-rocGroteskMedium text-[#142837]">
                                                    {truncate(po?.vendor?.profile?.businessName, {
                                                        length: 20,
                                                    })}
                                                </p>
                                            </div>
                                            <div className="flex mt-2 items-center space-x-2">
                                                <div className="flex items-center space-x-2">
                                                    <i className="ri-user-line text-[#64748B] "></i>
                                                    <p className="text-[#64748B] text-xs">
                                                        {po?.createdBy?.fullName}
                                                    </p>
                                                </div>
                                                <div className="border-r border-[#E2E8F0]"></div>
                                                <div className="flex items-center space-x-2">
                                                    <i className="ri-calendar-line text-[#64748B] "></i>
                                                    <p className="text-[#64748B] text-xs">
                                                        {moment(po?.issuedDate).format(
                                                            "MMM DD, YYYY"
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="3md:hidden flex mt-2 items-center space-x-2">
                                            <div
                                                className={` ${
                                                    po?.currentStatus?.toLowerCase() ===
                                                        "completed" && "bg-g-25  text-g-60 "
                                                } ${
                                                    po?.currentStatus?.toLowerCase() ===
                                                        "in-transit" &&
                                                    "bg-light-yellow-status  text-[#E5993E] "
                                                } 
                                                      ${
                                                          po?.currentStatus?.toLowerCase() ===
                                                              "rejected" && "bg-r-25  text-r-55 "
                                                      }
                                                      ${
                                                          po?.currentStatus?.toLowerCase() ===
                                                              "pending" &&
                                                          "bg-light-purple-2  text-[#6562E5] "
                                                      } rounded-[16px] py-[2px] px-[8px] `}
                                            >
                                                <div
                                                    className={` rounded-[16px] py-[2px] px-[8px]  ${
                                                        po?.currentStatus?.toLowerCase() ===
                                                            "completed" && "bg-g-25  text-g-60 "
                                                    } ${
                                                        po?.currentStatus?.toLowerCase() ===
                                                            "in-transit" &&
                                                        "bg-light-yellow-status  text-[#E5993E] "
                                                    }   ${
                                                        po?.currentStatus?.toLowerCase() ===
                                                            "rejected" && "bg-r-25  text-r-55 "
                                                    }   ${
                                                        po?.currentStatus?.toLowerCase() ===
                                                            "pending" &&
                                                        "bg-light-purple-2  text-[#6562E5] "
                                                    }
                                 `}
                                                >
                                                    <p className={`text-sm  font-rocGroteskMedium`}>
                                                        {po?.currentStatus?.toLowerCase() ===
                                                            "rejected" &&
                                                            truncate("Cancelled", {
                                                                length: 35,
                                                            })}
                                                        {po?.currentStatus?.toLowerCase() ===
                                                            "in-transit" &&
                                                            truncate("Pending fulfilment", {
                                                                length: 35,
                                                            })}
                                                        {po?.currentStatus?.toLowerCase() ===
                                                            "completed" &&
                                                            truncate("Fulfilled", {
                                                                length: 35,
                                                            })}
                                                        {po?.currentStatus?.toLowerCase() ===
                                                            "pending" &&
                                                            truncate("Pending", {
                                                                length: 35,
                                                            })}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="3md:flex hidden mt-2 items-center space-x-2">
                                        <div
                                            className={` ${
                                                po?.currentStatus?.toLowerCase() === "completed" &&
                                                "bg-g-25  text-g-60 "
                                            } ${
                                                po?.currentStatus?.toLowerCase() === "in-transit" &&
                                                "bg-light-yellow-status  text-[#E5993E] "
                                            } 
                                                      ${
                                                          po?.currentStatus?.toLowerCase() ===
                                                              "rejected" && "bg-r-25  text-r-55 "
                                                      }
                                                      ${
                                                          po?.currentStatus?.toLowerCase() ===
                                                              "pending" &&
                                                          "bg-light-purple-2  text-[#6562E5] "
                                                      } rounded-[16px] py-[2px] px-[8px] `}
                                        >
                                            <div
                                                className={` rounded-[16px] py-[2px] px-[8px]  ${
                                                    po?.currentStatus?.toLowerCase() ===
                                                        "completed" && "bg-g-25  text-g-60 "
                                                } ${
                                                    po?.currentStatus?.toLowerCase() ===
                                                        "in-transit" &&
                                                    "bg-light-yellow-status  text-[#E5993E] "
                                                }   ${
                                                    po?.currentStatus?.toLowerCase() ===
                                                        "rejected" && "bg-r-25  text-r-55 "
                                                }   ${
                                                    po?.currentStatus?.toLowerCase() ===
                                                        "pending" &&
                                                    "bg-light-purple-2  text-[#6562E5] "
                                                }
                                 `}
                                            >
                                                <p className={`text-sm font-rocGroteskMedium`}>
                                                    {po?.currentStatus?.toLowerCase() ===
                                                        "rejected" &&
                                                        truncate("Cancelled", {
                                                            length: 20,
                                                        })}
                                                    {po?.currentStatus?.toLowerCase() ===
                                                        "in-transit" &&
                                                        truncate("Pending fulfilment", {
                                                            length: 20,
                                                        })}
                                                    {po?.currentStatus?.toLowerCase() ===
                                                        "completed" &&
                                                        truncate("Fulfilled", {
                                                            length: 20,
                                                        })}
                                                    {po?.currentStatus?.toLowerCase() ===
                                                        "pending" &&
                                                        truncate("Pending", { length: 20 })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <i
                                            className="ri-more-2-fill cursor-pointer text-[#64748B] text-2xl"
                                            onClick={() =>
                                                navigate(
                                                    `/dashboard/purchase/shipment/${po?._id}`,
                                                    { state: { internalNavigation: true } }
                                                )
                                            }
                                        ></i>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {fetchingPurchaseOrderV2 && purchaseOrderList?.length > 1 && (
                        <div className="flex my-4 justify-center">
                            <Loader color="gm-25" size={4} />
                        </div>
                    )}
                    {purchaseOrderPagination?.current ===
                        purchaseOrderPagination?.number_of_pages && (
                        <div className="flex my-4 text-slate-500 justify-center text-sm font-rocGroteskMedium">
                            <p className="text-center"> End of list</p>
                        </div>
                    )}
                </div>
            </div>

            <FloatingActionBar
                totalSelected={selectedPO.length}
                title={`PO selected`}
                open={selectedPO?.length > 0}
                onClose={() => {
                    setSelectedPO([]);
                }}
            >
                <div className="flex items-center space-x-3">
                    {selectedPO?.every((item) => !item?.isArchived) && (
                        <Button
                            btnText="Archive"
                            btnPermission={[
                                "all_access",
                                "create_purchase_order",
                                "update_purchase_order",
                            ]}
                            btnClassname="!px-3 !py-[6px] !w-fit !text-[13px] !rounded-[4px] !bg-[#F4F5F7] !bg-n-20 !border-n-20 whitespace-nowrap"
                            icon={<i className="ri-inbox-archive-line !text-[#000]"></i>}
                            isLoading={archivingPurchaseOrder}
                            onClick={() => {
                                setSinglePOArchive([]);
                                setArchiveModal(true);
                            }}
                        />
                    )}

                    {selectedPO?.every((item) => item?.isArchived) && (
                        <Button
                            btnText="Unarchive"
                            btnPermission={[
                                "all_access",
                                "create_purchase_order",
                                "update_purchase_order",
                            ]}
                            btnClassname="!px-3 !py-[6px] !w-fit !text-[13px] !rounded-[4px] !bg-[#F4F5F7] !bg-n-20 !border-n-20 whitespace-nowrap"
                            icon={<i className="ri-inbox-archive-line !text-[#000]"></i>}
                            isLoading={unarchivingPurchaseOrder}
                            onClick={() => {
                                setSinglePOUnarchive([]);
                                setUnarchiveModal(true);
                            }}
                        />
                    )}

                    {selectedPO?.every((item) => !item?.isPinned) && (
                        <Button
                            btnText="Add to pin"
                            btnPermission={[
                                "all_access",
                                "create_purchase_order",
                                "update_purchase_order",
                            ]}
                            btnClassname="!px-3 !py-[6px] !w-fit !text-[13px] !rounded-[4px] !bg-[#F4F5F7] !bg-n-20 !border-n-20 whitespace-nowrap"
                            icon={<i className="ri-star-line !text-[#000]"></i>}
                            isLoading={isBulkPinning}
                            onClick={() => {
                                handleBulkPin(selectedPO.map((po) => po._id));
                            }}
                        />
                    )}
                    {selectedPO?.every((item) => item?.isPinned) && (
                        <Button
                            btnText="Remove from pin"
                            btnPermission={[
                                "all_access",
                                "create_purchase_order",
                                "update_purchase_order",
                            ]}
                            btnClassname="!px-3 !py-[6px] !w-fit !text-[13px] !rounded-[4px] !bg-[#F4F5F7] !bg-n-20 !border-n-20 whitespace-nowrap"
                            icon={<i className="ri-star-fill !text-[#FCD34D]"></i>}
                            isLoading={isBulkUnpinning}
                            onClick={() => {
                                handleBulkUnpin(selectedPO.map((po) => po._id));
                            }}
                        />
                    )}
                </div>
            </FloatingActionBar>

            <ModalContainer
                open={archiveModal}
                showCloseIcon={false}
                tailwindClassName="w-[500px]"
                closeModal={() => {
                    setArchiveModal(false);
                    setSinglePOArchive([]);
                }}
            >
                <div className="bg-white p-8 rounded-lg">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            singlePOArchive?.length > 0
                                ? handleBulkArchive(singlePOArchive)
                                : handleBulkArchive(selectedPO.map((po) => po._id));
                        }}
                    >
                        <p className="text-base font-rocGroteskMedium text-gm-50 mb-2">
                            Archive purchase order(s)?
                        </p>

                        <p className="text-sm font-rocGroteskMedium text-slate-500">
                            Are you sure you want to archive this Purchase Order(s)? It will be
                            removed from your active list but can still be found when you filter by
                            Archived POs.{" "}
                        </p>

                        <div className="pt-3 flex space-x-4 items-center justify-end ">
                            <Button
                                btnText="No, cancel"
                                btnClassname="!px-4 !py-2.5 !bg-tradeally-neutral-20 !w-fit "
                                onClick={() => {
                                    setArchiveModal(false);
                                }}
                            />
                            <Button
                                btnText="Yes, archive PO"
                                btnClassname="!px-4 !py-2.5 !w-fit "
                                type={"submit"}
                                isLoading={archivingPurchaseOrder}
                            />
                        </div>
                    </form>
                </div>
            </ModalContainer>

            <ModalContainer
                open={unarchiveModal}
                showCloseIcon={false}
                tailwindClassName="w-[500px]"
                closeModal={() => {
                    setUnarchiveModal(false);
                    setSinglePOUnarchive([]);
                }}
            >
                <div className="bg-white p-8 rounded-lg">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            singlePOUnarchive?.length > 0
                                ? handleBulkUnarchive(singlePOUnarchive)
                                : handleBulkUnarchive(selectedPO.map((po) => po._id));
                        }}
                    >
                        <p className="text-base font-rocGroteskMedium text-gm-50 mb-2">
                            Restore purchase order(s)?
                        </p>

                        <p className="text-sm font-rocGroteskMedium text-slate-500">
                            Do you want to restore this Purchase Order(s) back to your active list?
                            It will reappear as an active POs for easier access and management.
                        </p>

                        <div className="pt-3 flex space-x-4 items-center justify-end ">
                            <Button
                                btnText="No, cancel"
                                btnClassname="!px-4 !py-2.5 !bg-tradeally-neutral-20 !w-fit "
                                onClick={() => {
                                    setUnarchiveModal(false);
                                }}
                            />
                            <Button
                                btnText="Yes, restore PO"
                                btnClassname="!px-4 !py-2.5 !w-fit "
                                type={"submit"}
                                isLoading={unarchivingPurchaseOrder}
                            />
                        </div>
                    </form>
                </div>
            </ModalContainer>
        </>
    );
};

export default PurchaseOrder;
